import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
}

export const SEO = ({ 
  title, 
  description, 
  canonical, 
  keywords,
  ogType = "website",
  ogImage = "https://numbers-generator.com/icon-512.png"
}: SEOProps) => {
  // Add RandomGen branding if not already present
  const fullTitle = title.includes("RandomGen") || title.includes("Random Number Generator")
    ? title 
    : `${title} | RandomGen - Random Number Generator`;
  
  // Ensure canonical URL is always absolute and uses HTTPS
  const baseUrl = "https://numbers-generator.com";
  const pathname = canonical || window.location.pathname;
  const canonicalUrl = pathname.startsWith('http') 
    ? pathname 
    : `${baseUrl}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
  
  // Don't render duplicate canonical if we're on homepage and index.html already has one
  const isHomepage = window.location.pathname === '/' || window.location.pathname === '';
  
  // Use full URL for OG image if relative path provided
  const fullOgImage = ogImage.startsWith('http') 
    ? ogImage 
    : `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {!isHomepage && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="RandomGen" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Alternate for mobile */}
      {!isHomepage && <link rel="alternate" media="only screen and (max-width: 640px)" href={canonicalUrl} />}
    </Helmet>
  );
};